import type { ClassValue } from "class-variance-authority/types";
import { useEffect, useRef, useState, cloneElement } from "react";
import { createPortal } from "react-dom";

import { usePortalMount } from "@/hooks/portal";

interface ModalProps {
  children: [React.ReactElement<any>, React.ReactElement<any>];
  onClose?: () => void;
  onOpen?: () => void;
}

interface EnhancedChildProps {
  onClick: () => void;
  className?: ClassValue[];
}

function Modal({ children, onClose, onOpen }: ModalProps) {
  const [is_active, setActive] = useState(false);

  const modal_element = usePortalMount("modal");

  const _ref = useRef<HTMLDivElement>(null);

  const toggleFn = () => {
    if (is_active) {
      onClose?.();
    } else {
      onOpen?.();
    }
    setActive(!is_active);
  };

  useEffect(() => {
    const modalHandleClickOutside = (event: PointerEvent): void => {
      if (_ref.current && !_ref.current.contains(event.target as Node)) {
        setActive(false);
        onClose?.();
      }
    };
    document.addEventListener("pointerdown", modalHandleClickOutside, true);
    return () => {
      document.removeEventListener("pointerdown", modalHandleClickOutside, true);
    };
  }, [onClose]);

  useEffect(() => {
    if (is_active) {
      document.body.classList.add("overflow-hidden");
      if (modal_element) {
        modal_element.classList.add("overflow-y-auto");
        modal_element.classList.add("modal-is-active");
      }
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
      if (modal_element) {
        modal_element.classList.remove("modal-is-active");
        modal_element.classList.remove("overflow-y-auto");
      }
    };
  }, [is_active, modal_element]);

  const [first_child, second_child] = children;

  return (
    <>
      {
        cloneElement(first_child, {
          onClick: toggleFn,
        }) as unknown as EnhancedChildProps
      }
      {is_active &&
        modal_element &&
        createPortal(
          <div className="modal-window overscroll-contain" ref={_ref}>
            {cloneElement(second_child, {
              is_active,
              toggleFn,
            })}
          </div>,
          modal_element,
          "modal",
        )}
    </>
  );
}

export default Modal;
